import { ref } from 'vue'
import { useStore } from 'vuex'
import { differenceInDays } from 'date-fns'

import bus from '@/plugins/eventbus'
import { useDeviceAware } from '@/composables/useDeviceAware'
import { useExtensionPresence } from '@/composables/useExtensionPresence'
import {
  MIN_ITEMS,
  MIN_ACCOUNT_AGE_DAYS,
  REASK_COOLDOWN_MS,
  IS_STAGING,
  MAX_SHOWS,
  SETTINGS_KEY,
  EVENTS,
  TARGET,
  IOS_APP_ID,
  EXTENSION_BROWSERS,
} from '@/constants/ratingPrompt'

// One soft-ask per app session, regardless of surface.
let shownThisSession = false

// A trigger requested on a surface that must NOT show the prompt in place (e.g.
// the add-details page). It is held until the user navigates away and is then
// flushed onto the destination page. If the surface closes the app instead
// (extension popup window.close()), it is never flushed -> no prompt.
let pendingTrigger = null

// Maps a browser extension-store base URL (CONFIG.storeLinks[linkKey]) to its
// "rate / leave a review" page so a positive answer lands on the review surface
// rather than the plain listing.
function reviewUrlForBrowser(linkKey, storeUrl) {
  if (!storeUrl) return storeUrl
  const base = storeUrl.replace(/\/+$/, '') // trim trailing slash for consistent joins
  switch (linkKey) {
    // Chrome Web Store: reviews tab lives at <detail>/reviews
    case 'chrome': return `${base}/reviews`
    // Firefox Add-ons (AMO): <addon>/reviews/
    case 'firefox': return `${base}/reviews/`
    // Safari extension ships via the App Store: write-review deep link
    case 'safari': return `${storeUrl}${storeUrl.includes('?') ? '&' : '?'}action=write-review`
    // Edge Add-ons has no dedicated review URL; ratings live on the detail page.
    case 'edge':
    default: return storeUrl
  }
}

// Modal surface state. The singleton <RatingPromptDialog> (mounted in App.vue,
// a DialogHybrid so it's a bottom sheet on mobile) and the flow share one
// open/await channel at module scope.
const dialogOpen = ref(false)
let dialogResolve = null

// Opens the dialog and resolves with the user's choice
// ('positive' | 'feedback' | 'negative') or null when dismissed.
function openRatingDialog() {
  return new Promise(resolve => {
    dialogResolve = resolve
    dialogOpen.value = true
  })
}

// Called by <RatingPromptDialog> on answer or dismiss. Guarded so the
// dismiss-on-close path can't double-settle after a button already answered.
function settleRatingDialog(result) {
  if (!dialogResolve) return
  const resolve = dialogResolve
  dialogResolve = null
  dialogOpen.value = false
  resolve(result)
}

// Consumed by <RatingPromptDialog>.
export function useRatingDialog() {
  return { dialogOpen, settleRatingDialog }
}

/**
 * In-app rating prompt logic (two-step soft-ask). Resolves the review target
 * for the current client, gates on engagement + per-target history, and runs
 * the native review (mobile) or store redirect (extension/webapp). State lives
 * in the backend user settings, scoped per target.
 */
export function useRatingPrompt() {
  const store = useStore()
  const { isNative, isWebApp, deviceInfo } = useDeviceAware()
  const { checkPresence } = useExtensionPresence()

  const session = () => store.getters['auth/session']

  // --- target resolution -----------------------------------------------------

  // Returns { target, storeUrl, reviewUrl } for the current client, or null when
  // there is no rateable target (e.g. plain webapp, or an unknown browser).
  // reviewUrl is where a positive answer should land the user (review page);
  // storeUrl is the plain listing used as a native fallback.
  function resolveTarget() {
    if (isNative.value) {
      const os = deviceInfo.value.platform
      if (os === 'ios') return { target: TARGET.IOS, storeUrl: CONFIG.storeLinks.ios, reviewUrl: CONFIG.storeLinks.ios }
      if (os === 'android') return { target: TARGET.ANDROID, storeUrl: CONFIG.storeLinks.android, reviewUrl: CONFIG.storeLinks.android }
      return null
    }

    // extension popup OR webapp -> the browser's extension store review page
    const browser = (deviceInfo.value.browser || '').toLowerCase()
    const match = EXTENSION_BROWSERS.find(b => browser.includes(b.match))
    if (!match) return null
    const storeUrl = CONFIG.storeLinks[match.linkKey]
    return { target: match.target, storeUrl, reviewUrl: reviewUrlForBrowser(match.linkKey, storeUrl) }
  }

  // --- engagement + history gating -------------------------------------------

  function isEligible() {
    const user = session()
    if (!user) return false
    if ((user.itemsCount ?? 0) < MIN_ITEMS) return false
    if (!user.createdAt) return false
    return differenceInDays(new Date(), new Date(user.createdAt)) >= MIN_ACCOUNT_AGE_DAYS
  }

  async function readState() {
    try {
      const settings = await store.dispatch('user/getSettings')
      const entry = settings?.find(s => s.name === SETTINGS_KEY)
      return entry?.value ? JSON.parse(entry.value) : {}
    } catch {
      return {}
    }
  }

  async function writeState(state) {
    await store.dispatch('user/editSettings', { [SETTINGS_KEY]: JSON.stringify(state) })
  }

  function canShowTarget(state, target) {
    const t = state[target]
    if (!t) return true
    // On staging the terminal gates are bypassed so the full flow can be tested
    // repeatedly (e.g. after opening the store link, which marks `completed`).
    // Only the short re-ask cooldown applies. Production keeps both hard stops.
    if (!IS_STAGING) {
      if (t.completed) return false
      if ((t.shows ?? 0) >= MAX_SHOWS) return false
    }
    if (t.lastShownAt && (Date.now() - new Date(t.lastShownAt).getTime()) < REASK_COOLDOWN_MS) {
      return false
    }
    return true
  }

  async function recordShown(target, trigger) {
    const state = await readState()
    const t = state[target] ?? { shows: 0, completed: false }
    state[target] = { ...t, shows: (t.shows ?? 0) + 1, lastShownAt: new Date().toISOString() }
    await writeState(state)
    bus.emit(EVENTS.SHOWN, { target, trigger, shows: state[target].shows })
  }

  async function markCompleted(target) {
    const state = await readState()
    state[target] = { ...(state[target] ?? { shows: 0 }), completed: true }
    await writeState(state)
  }

  // --- outcomes --------------------------------------------------------------

  // Positive: native review sheet (mobile) or open the store review page
  // (ext/webapp). reviewUrl is the browser store's review surface; falls back to
  // the plain listing (storeUrl) when no review URL exists.
  async function accept({ target, storeUrl, reviewUrl }, trigger) {
    bus.emit(EVENTS.ACCEPTED, { target, trigger })
    await markCompleted(target)

    const url = reviewUrl || storeUrl

    if (target === TARGET.IOS || target === TARGET.ANDROID) {
      try {
        const { AppReview } = await import('@capawesome/capacitor-app-review')
        await AppReview.requestReview()
      } catch {
        // OS declined to show the sheet (rate-limited) -> fall back to the store
        try {
          const { AppReview } = await import('@capawesome/capacitor-app-review')
          await AppReview.openAppStore({ appId: IOS_APP_ID })
        } catch {
          if (url) window.open(url, '_blank', 'noopener')
        }
      }
      return
    }

    if (url) window.open(url, '_blank', 'noopener')
  }

  // Negative answer -> route to feedback (or just close). We do NOT mark the
  // target completed here: a "Not really" / "No thanks" is not a finished review,
  // so the user can be re-asked later (subject to the cooldown + MAX_SHOWS cap).
  // Only a positive answer (accept) completes the target.
  async function decline({ target, feedback, trigger }) {
    bus.emit(EVENTS.DECLINED, { target, trigger, feedback: !!feedback })
    if (feedback) window.open(`${CONFIG.landingpageUrl}/contact`, '_blank', 'noopener')
  }

  // --- orchestration ---------------------------------------------------------

  // True when we may display the prompt right now for `target` (eligible,
  // not yet shown this session, not capped/completed/cooling-down).
  async function shouldPrompt(target) {
    if (shownThisSession) return false
    if (!isEligible()) return false
    const state = await readState()
    return canShowTarget(state, target)
  }

  // Modal surface (mobile, webapp AND extension). Resolves target, gates, opens
  // the RatingPromptDialog (DialogHybrid) and acts on the result. Returns true
  // if the dialog was shown.
  async function openModalFlow(trigger) {
    // Modal surface for ALL clients now (mobile, webapp, extension). The
    // extension previously used an inline banner; it uses this dialog too.
    // `trigger` (RATING_TRIGGER.*) names the page/moment that asked, for
    // analytics; gating is identical regardless of trigger.
    const resolved = resolveTarget()
    if (!resolved) return false
    if (!(await shouldPrompt(resolved.target))) return false

    // On the plain webapp we only nudge users who actually have the matching
    // extension installed (the prompt sends them to that extension's store).
    if (isWebApp.value && !(await checkPresence())) return false

    shownThisSession = true
    await recordShown(resolved.target, trigger)

    const result = await openRatingDialog() // 'positive' | 'feedback' | 'negative' | null

    if (result === 'positive') await accept(resolved, trigger)
    else if (result === 'feedback') await decline({ target: resolved.target, trigger, feedback: true })
    else if (result === 'negative') await decline({ target: resolved.target, trigger, feedback: false })
    // null = dismissed -> cooldown already recorded by recordShown
    return true
  }

  // Queue the prompt without showing it. Used by surfaces that must not display
  // it in place (e.g. the add-details page) -> flushed after navigation.
  function requestRatingPrompt(trigger) {
    pendingTrigger = trigger
  }

  // Show a queued prompt, if any. Call after a route change so it lands on the
  // destination page rather than the surface that requested it.
  function flushPendingRating() {
    if (!pendingTrigger) return Promise.resolve(false)
    const trigger = pendingTrigger
    pendingTrigger = null
    return openModalFlow(trigger)
  }

  return {
    resolveTarget,
    isEligible,
    shouldPrompt,
    recordShown,
    accept,
    decline,
    openModalFlow,
    requestRatingPrompt,
    flushPendingRating,
    markShownThisSession: () => { shownThisSession = true },
  }
}
